import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';

import { _sortBy } from 'underscore-es';
import moment from 'moment';

import { MenuItem, FormControlLabel, Checkbox } from '@material-ui/core';

import { useToggle } from '../../../../../libs/react-use-extra';
import { isDefined, mergeDeep } from '../../../../../libs/js-utils';
import {
  validateStageName,
  validateStageTemplate,
  validateCondition,
} from '../../../utils/stageValidation';
import ConfirmationDialog from '../../../ConfirmationDialog';
import {
  getOrgAssociations,
  hasOrgAssociations,
  isAmsSourcedOrg,
  OrganizationType,
  isNotGZ,
} from '../../../../../redux/services/organization';
import { selectedOrganizationSelector } from '../../../../../redux/selectors/organization';
import { Order } from '../../../api';
import Button from '../../Button';
import { useEditor } from '../../CampaignEditor';
import {
  Body,
  ContentBody,
  ConditionPicker,
  ConditionPickerField,
  Container,
  CopyButton,
  DeleteButton,
  EditButton,
  ExpandButton,
  ArrangeButton,
  Header,
  NameInput,
  Section,
  TemplateButtonWrapper,
  TemplateName,
  TemplateEditor,
} from './Stage.styled';
import { ValidityIndicator } from '../../../forms';
import { useToast } from '../../../../../redux/actions/UI';
import { useMeasure } from 'react-use';
import {
  CalendarIcon,
  SendIcon,
  CollapseIcon,
  ExpandIcon,
  DeleteIcon,
  CopyIcon,
  ArrangeIcon,
} from '../../../SVGs/Icons';
import Thumbnail from './HtmlThumbnail';
import generateObjectId from '../../../utils/generateObjectId';
import { generateMembershipMap } from '../../../../../redux/helpers/helpers';

const Unit = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
};

const Event = {
  JOIN_DATE: 'joinDate',
  BIRTH_DATE: 'birthDate',
  ORIENTATION_DATE: 'orientationDate',
  LICENSE_EXPIRATION_DATE: 'licenseExpirationDate',
  CREDIT_CARD_EXPIRATION_DATE: 'creditCardExpirationDate',
  LAST_PREFERENCE_PAGE_VISITED_DATE: 'lastPreferencePageVisitedDate',
  ADDED_DATE: 'addedDate',
  COE_DATE: 'coeDate',
  CUSTOM: 'custom',
};

const OrderVariant = {
  BOTH: 'both',
  BEFORE_ONLY: 'before-only',
  AFTER_ONLY: 'after-only',
};

const ORDERS_BY_ORDER_VARIANT = {
  [OrderVariant.BOTH]: [Order.BEFORE, Order.AFTER],
  [OrderVariant.BEFORE_ONLY]: [Order.BEFORE],
  [OrderVariant.AFTER_ONLY]: [Order.AFTER],
};

const ORDER_VARIANT_BY_EVENT = {
  [Event.LAST_PREFERENCE_PAGE_VISITED_DATE]: OrderVariant.AFTER_ONLY,
  [Event.JOIN_DATE]: OrderVariant.AFTER_ONLY,
  [Event.ADDED_DATE]: OrderVariant.AFTER_ONLY,
};

const LABELS_BY_EVENT = {
  [Event.JOIN_DATE]: 'Join Date',
  [Event.BIRTH_DATE]: 'Birthday',
  [Event.ORIENTATION_DATE]: 'Orientation Date',
  [Event.LICENSE_EXPIRATION_DATE]: 'License Expiration Date',
  [Event.CREDIT_CARD_EXPIRATION_DATE]: 'Credit Card Expiration Date',
  [Event.LAST_PREFERENCE_PAGE_VISITED_DATE]: 'Last Preference Page Visit Date',
  [Event.ADDED_DATE]: 'Added Date',
  [Event.COE_DATE]: 'Code of Ethics Expiration Date',
  [Event.CUSTOM]: 'Custom Date',
};

const AVAILABILITY_BY_EVENT = {
  [Event.JOIN_DATE]: [isAmsSourcedOrg, hasOrgAssociations],
  [Event.BIRTH_DATE]: true,
  [Event.ADDED_DATE]: true,
  [Event.COE_DATE]: org =>
    [OrganizationType.RAMCO, OrganizationType.MAGIC].includes(org.type),
  [Event.ORIENTATION_DATE]: [isAmsSourcedOrg, hasOrgAssociations, isNotGZ],
  [Event.LICENSE_EXPIRATION_DATE]: isAmsSourcedOrg,
  [Event.LAST_PREFERENCE_PAGE_VISITED_DATE]: true,
  [Event.CREDIT_CARD_EXPIRATION_DATE]: org =>
    org.type === OrganizationType.MAGIC,
  [Event.CUSTOM]: true,
};

const getOrgAssociationsAsOptions = org => {
  const memberships = getOrgAssociations(org);
  return memberships.map(mship => ({
    label: mship.name,
    value: mship.amsId,
  }));
};

const OPTION_GETTERS_BY_EVENT = {
  [Event.JOIN_DATE]: getOrgAssociationsAsOptions,
  [Event.ORIENTATION_DATE]: getOrgAssociationsAsOptions,
};

const useMembershipMap = () => {
  const org = useSelector(selectedOrganizationSelector);
  return generateMembershipMap(org.memberships);
};

const useEvents = () => {
  const org = useSelector(selectedOrganizationSelector);
  return Object.values(Event).filter(event => {
    const availability = AVAILABILITY_BY_EVENT[event];
    if (Array.isArray(availability)) {
      return availability.every(isAvailable => isAvailable(org));
    } else if (typeof availability === 'function') {
      if (event === 'coeDate' && org.type === 'magic' && !org.magicCOEEnabled)
        return false;
      return availability(org);
    } else if (typeof availability === 'boolean') {
      return availability;
    }

    return false;
  });
};

const useOptions = event => {
  const org = useSelector(selectedOrganizationSelector);

  if (!event) {
    return [];
  }

  const optionGetter = OPTION_GETTERS_BY_EVENT[event];
  if (optionGetter) {
    return optionGetter(org);
  }

  return [];
};

const useCustomDate = event => {
  return event === Event.CUSTOM;
};

const useUpdater = (value, onChange) => {
  const update = useCallback(
    updates => {
      onChange(mergeDeep(value, updates));
    },
    [onChange, value]
  );

  const updateName = useCallback(
    newName => {
      update({ name: newName });
    },
    [update]
  );

  const updateDelay = useCallback(
    newDelay => {
      update({
        conditions: [
          { delay: { value: newDelay ? Math.abs(newDelay) : newDelay } },
        ],
      });
    },
    [update]
  );

  const updateOrder = useCallback(
    newOrder => {
      update({
        conditions: [{ delay: { order: newOrder } }],
      });
    },
    [update]
  );

  const updateUnit = useCallback(
    newUnit => {
      update({
        conditions: [{ delay: { unit: newUnit } }],
      });
    },
    [update]
  );

  const updateEvent = useCallback(
    newEvent => {
      // Safely get condition and delay, with defaults
      const condition = value.conditions?.[0];
      const delay = condition?.delay || {
        value: 0,
        order: Order.AFTER,
        unit: Unit.DAY,
      };
      const { order, value: delayValue, unit } = delay;

      const newOrderVariant =
        ORDER_VARIANT_BY_EVENT[newEvent] ?? OrderVariant.BOTH;
      const newAllowedOrders = ORDERS_BY_ORDER_VARIANT[newOrderVariant];

      const conditionUpdates = {
        _id: condition?._id || generateObjectId(),
        name: newEvent,
        delay: { value: delayValue, order, unit },
      };

      if (!newAllowedOrders.includes(order)) {
        const newOrder = newAllowedOrders[0];
        conditionUpdates.delay = { value: delayValue, order: newOrder, unit };
      }

      // there is no need for an option to exists unless it is a joinDate or orientationDate
      if (!OPTION_GETTERS_BY_EVENT[newEvent]) {
        conditionUpdates.option = undefined;
      }

      update({
        conditions: [conditionUpdates],
      });
    },
    [update, value.conditions]
  );

  const updateOption = useCallback(
    newOptions => {
      update({
        conditions: [{ option: { names: newOptions } }],
      });
    },
    [update]
  );

  const updateOptionCheck = useCallback(
    isAnyChecked => {
      update({
        conditions: [
          { option: { anySelected: isAnyChecked, names: undefined } },
        ],
      });
    },
    [update]
  );

  const updateCustomDate = useCallback(
    newCustomDate => {
      update({
        conditions: [{ date: newCustomDate }],
      });
    },
    [update]
  );

  return useMemo(
    () => ({
      name: updateName,
      delay: updateDelay,
      unit: updateUnit,
      order: updateOrder,
      event: updateEvent,
      option: updateOption,
      optionCheck: updateOptionCheck,
      customDate: updateCustomDate,
    }),
    [
      updateDelay,
      updateEvent,
      updateName,
      updateOption,
      updateOrder,
      updateUnit,
      updateCustomDate,
    ]
  );
};

const useValidator = ({ name, condition, template }) => {
  // Use shared validation functions with isDefined helper for delay validation
  const validateNameWithShared = value => validateStageName(value);
  const validateTemplateWithShared = value => validateStageTemplate(value);
  const validateConditionWithShared = value =>
    validateCondition(value, isDefined);

  const fieldValidators = {
    name: validateNameWithShared,
    template: validateTemplateWithShared,
    condition: validateConditionWithShared,
  };

  const getValidatorFor = field => {
    return fieldValidators[field];
  };

  const validStates = {
    name: getValidatorFor('name')(name),
    template: getValidatorFor('template')(template),
    condition: getValidatorFor('condition')(condition),
  };

  const check = (field = null) => {
    if (!field) {
      return check('name') && check('template') && check('condition');
    }

    return validStates[field];
  };

  return { check, for: getValidatorFor };
};

const Stage = (
  {
    className,
    value,
    onChange,
    isValidationVisible: _isValidationVisible = false,
    shouldAllowDeletingStages = true,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDrop,
    isDragging = false,
  },
  ref
) => {
  const updater = useUpdater(value, onChange);
  const editor = useEditor();
  const { errorToast } = useToast();
  const history = useHistory();
  const location = useLocation();

  const [
    isDeleteConfirmationOpen,
    { on: openDeleteConfirmation, off: closeDeleteConfirmation },
  ] = useToggle();

  const nameInputRef = useRef(null);
  const [conditionPickerWrapperRef, { width: conditionPickerWidth }] =
    useMeasure();

  const [isExpanded, { toggle: toggleExpanded }] = useToggle(true);
  const [isCollapseTried, { on: markCollapseTried }] = useToggle(false);
  const isValidationVisible = _isValidationVisible || isCollapseTried;

  const { name, template, conditions = [] } = value;
  const condition = conditions[0]; // Safely get first condition, may be undefined
  const validator = useValidator({ name, condition, template });

  useImperativeHandle(
    ref,
    () => ({
      isValid() {
        return validator.check();
      },
    }),
    [validator]
  );

  // Safely destructure condition with defaults
  const { delay: compositeDelay, name: event } = condition || {};
  const option = condition?.option?.names || [];
  const isOptionAnyChecked = condition?.option?.anySelected || false;

  // Safely destructure delay with defaults
  const {
    value: delay,
    order,
    unit,
  } = compositeDelay || { value: 0, order: Order.AFTER, unit: Unit.DAY };
  const orderVariant = event
    ? ORDER_VARIANT_BY_EVENT[event] ?? OrderVariant.BOTH
    : OrderVariant.BOTH;
  const allowedOrders = ORDERS_BY_ORDER_VARIANT[orderVariant];
  const isBeforeOrderAllowed = allowedOrders.includes(Order.BEFORE);
  const isAfterOrderAllowed = allowedOrders.includes(Order.AFTER);

  const handleNameChange = useCallback(
    event => {
      const newName = event.target.value;
      updater.name(newName.length === 0 ? null : newName);
    },
    [updater]
  );

  const handleDelayChange = useCallback(
    event => {
      const newDelayRaw = event.target.value;
      if (!newDelayRaw) {
        return;
      }

      const newDelay = Number(newDelayRaw);
      const isNewDelayValidNumber = !Number.isNaN(newDelay);
      if (isNewDelayValidNumber) {
        updater.delay(newDelay);
      }
    },
    [updater]
  );

  const handleUnitChange = useCallback(
    event => {
      const newUnit = event.target.value;
      updater.unit(newUnit);
    },
    [updater]
  );

  const handleOrderChange = useCallback(
    event => {
      const newOrder = event.target.value;
      updater.order(newOrder);
    },
    [updater]
  );

  const handleEventChange = useCallback(
    event => {
      updater.event(event.target.value);
    },
    [updater]
  );

  const handleCustomDateChange = useCallback(
    event => {
      updater.customDate(event);
    },
    [updater]
  );

  const handleOptionChange = useCallback(
    event => {
      updater.option(event.target.value);
    },
    [updater]
  );

  const handleOptionCheck = useCallback(
    event => {
      updater.optionCheck(event.target.checked);
    },
    [updater]
  );

  const openTemplate = useCallback(async () => {
    const params = { stage: value._id };
    if (template) {
      params.template = template;
    }

    // Update URL to include stageId as query parameter
    const currentQueryParams = queryString.parse(location.search);
    const newQueryParams = {
      ...currentQueryParams,
      step: 'template',
      stageId: value._id,
    };
    const queryStringStr = queryString.stringify(newQueryParams);
    history.replace(`${location.pathname}?${queryStringStr}`);

    // goToStep will call onBeforeStepChange which saves the campaign
    // This ensures the stage is saved before opening the template editor
    await editor.goToStep('template/editor', params);
  }, [editor, value._id, template]);

  const handleExpandButtonClick = useCallback(() => {
    if (!isCollapseTried && isExpanded) {
      markCollapseTried();
    }

    const isValid = validator.check();
    if (isValid || !isExpanded) {
      toggleExpanded();
    }

    if (!isValid) {
      errorToast(
        "Can't collapse a stage with invalid fields. Please correct them first.",
        5
      );
    }
  }, [
    errorToast,
    isCollapseTried,
    isExpanded,
    markCollapseTried,
    toggleExpanded,
    validator,
  ]);

  const focusNameInput = useCallback(() => {
    nameInputRef.current.focus();
  }, []);

  const deleteStage = useCallback(() => {
    const stages = editor.getField('stages') ?? [];
    const newStages = stages.filter(_stage => _stage._id !== value._id);
    editor.setField('stages', newStages);
  }, [editor, value._id]);

  const copyStage = useCallback(() => {
    const stages = editor.getField('stages') ?? [];
    const currentStageIndex = stages.findIndex(
      _stage => _stage._id === value._id
    );

    if (currentStageIndex === -1) {
      return;
    }

    // Create a deep copy of the stage
    const copiedStage = {
      ...value,
      _id: generateObjectId(),
      name: `Copy of ${value.name}`,
      conditions: value.conditions.map(condition => ({
        ...condition,
        _id: generateObjectId(),
      })),
    };

    // Insert the copied stage right after the current stage
    const newStages = [
      ...stages.slice(0, currentStageIndex + 1),
      copiedStage,
      ...stages.slice(currentStageIndex + 1),
    ];

    editor.setField('stages', newStages);
  }, [editor, value]);

  const handleCopyClick = useCallback(() => {
    copyStage();
  }, [copyStage]);

  const handleDeleteClick = useCallback(() => {
    openDeleteConfirmation();
  }, [openDeleteConfirmation]);

  const events = useEvents();
  const sortedEvents = _sortBy(events, event => LABELS_BY_EVENT[event]);
  const options = useOptions(event);
  const isCustomDate = useCustomDate(event);
  const membershipMap = useMembershipMap();

  const handleDragStart = useCallback(
    e => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', value._id);
      if (onDragStart) {
        onDragStart();
      }
    },
    [onDragStart, value._id]
  );

  const handleDragEnd = useCallback(() => {
    if (onDragEnd) {
      onDragEnd();
    }
  }, [onDragEnd]);

  const handleContainerDragOver = useCallback(e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (onDragOver) onDragOver(e);
  }, []);

  const handleContainerDrop = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      if (onDrop) onDrop(e);
    },
    [onDrop]
  );

  return (
    <Container
      className={className}
      isDragging={isDragging}
      onDragOver={handleContainerDragOver}
      onDrop={handleContainerDrop}
    >
      <ArrangeButton
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <ArrangeIcon />
      </ArrangeButton>
      <Header isExpanded={isExpanded}>
        <Header.Left>
          <NameInput
            inputRef={nameInputRef}
            value={name ?? ''}
            onChange={handleNameChange}
            placeholder="Enter Stage Name"
          />
          <EditButton onClick={focusNameInput} />
          <EditButton.Text onClick={focusNameInput}>Edit name</EditButton.Text>
        </Header.Left>
        <Header.Right>
          <CopyButton onClick={handleCopyClick}>
            <CopyIcon />
          </CopyButton>
          {shouldAllowDeletingStages && (
            <DeleteButton onClick={handleDeleteClick}>
              <DeleteIcon />
            </DeleteButton>
          )}
          <ExpandButton onClick={handleExpandButtonClick}>
            {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
          </ExpandButton>
          {isValidationVisible && (
            <ValidityIndicator
              isValid={validator.check()}
              isVisible={isValidationVisible}
            />
          )}
        </Header.Right>
      </Header>
      {isExpanded && (
        <Body>
          <ContentBody>
            <Section>
              <Section.Icon as={CalendarIcon} />
              <Section.Title>
                When the following condition is{' '}
                <Section.Title.Highlight>true</Section.Title.Highlight> for a
                contact in the Audience...
              </Section.Title>
              <Section.Content>
                <ConditionPickerField ref={conditionPickerWrapperRef}>
                  <ConditionPicker>
                    <ConditionPicker.DelayInput
                      inputRef={(() => {
                        let node = null;

                        return _node => {
                          const focusListener = event => {
                            event.target.select();
                          };

                          if (_node) {
                            node = _node;
                            node.addEventListener('focus', focusListener);
                          } else {
                            node.removeEventListener('focus', focusListener);
                          }
                        };
                      })()}
                      value={delay ?? ''}
                      onChange={handleDelayChange}
                    />
                    <ConditionPicker.UnitSelect
                      value={unit ?? ''}
                      onChange={handleUnitChange}
                    >
                      <MenuItem value={Unit.DAY}>day(s)</MenuItem>
                      <MenuItem value={Unit.WEEK}>week(s)</MenuItem>
                      <MenuItem value={Unit.MONTH}>month(s)</MenuItem>
                      <MenuItem value={Unit.YEAR}>year(s)</MenuItem>
                    </ConditionPicker.UnitSelect>
                    <ConditionPicker.OrderSelect
                      value={order ?? ''}
                      onChange={handleOrderChange}
                    >
                      {isAfterOrderAllowed && (
                        <MenuItem value={Order.AFTER}>after</MenuItem>
                      )}
                      {isBeforeOrderAllowed && (
                        <MenuItem value={Order.BEFORE}>before</MenuItem>
                      )}
                    </ConditionPicker.OrderSelect>
                    <ConditionPicker.EventSelect
                      value={event ?? ''}
                      onChange={handleEventChange}
                      displayEmpty
                    >
                      <MenuItem style={{ display: 'none' }} value="">
                        [Event]
                      </MenuItem>
                      {sortedEvents.map(event => {
                        return (
                          <MenuItem key={event} value={event}>
                            {LABELS_BY_EVENT[event]}
                          </MenuItem>
                        );
                      })}
                    </ConditionPicker.EventSelect>
                    {isCustomDate && (
                      <>
                        <ConditionPicker.OptionSeparator>
                          of
                        </ConditionPicker.OptionSeparator>
                        <ConditionPicker.CustomDateInput
                          format="YYYY-MM-DD"
                          value={
                            condition?.date ? moment(condition?.date) : null
                          }
                          onChange={(date, dateString) => {
                            handleCustomDateChange(dateString);
                          }}
                        />
                      </>
                    )}
                    {options.length > 0 && (
                      <>
                        <ConditionPicker.OptionSeparator>
                          of
                        </ConditionPicker.OptionSeparator>
                        <ConditionPicker.OptionSelect
                          value={option ?? []}
                          onChange={handleOptionChange}
                          placeholder="Option"
                          displayEmpty
                          multiple
                          disabled={isOptionAnyChecked}
                          renderValue={selected => {
                            if (selected.length === 0 && isOptionAnyChecked) {
                              return 'Any/All';
                            }

                            const mappedNameValues = selected.map(
                              name => membershipMap[name] || name
                            );
                            return mappedNameValues.join(' or ');
                          }}
                        >
                          {options.map(({ value, label }) => {
                            return (
                              <MenuItem key={value} value={value}>
                                {label}
                              </MenuItem>
                            );
                          })}
                        </ConditionPicker.OptionSelect>
                        <ConditionPicker.OptionSeparator>
                          or
                        </ConditionPicker.OptionSeparator>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={isOptionAnyChecked}
                              onChange={handleOptionCheck}
                              label="Any/all"
                              disabled={option.length}
                            />
                          }
                          label="Any/all"
                        />
                      </>
                    )}
                  </ConditionPicker>
                  <ValidityIndicator
                    isVisible={isValidationVisible}
                    isValid={validator.check('condition')}
                  />
                </ConditionPickerField>
              </Section.Content>
            </Section>
            <Section>
              <Section.Icon as={SendIcon} />
              <Section.Title>
                ... Send a message to that contact using this template:
              </Section.Title>
              <Section.Content>
                <TemplateEditor>
                  {template && validator.check('template') && (
                    <TemplateName
                      width={conditionPickerWidth}
                      value={template.name}
                      isValidationVisible={isValidationVisible}
                      validate={() => validator.for('template')(template)}
                    />
                  )}
                  <TemplateButtonWrapper>
                    <Button onClick={openTemplate}>
                      {template && validator.check('template')
                        ? 'Change'
                        : 'Select'}{' '}
                      Message
                    </Button>
                    {!validator.check('template') && (
                      <ValidityIndicator
                        isVisible={isValidationVisible}
                        isValid={validator.check('template')}
                      />
                    )}
                  </TemplateButtonWrapper>
                </TemplateEditor>
              </Section.Content>
            </Section>
          </ContentBody>
          <Thumbnail html={value?.template?.html} />
        </Body>
      )}
      {isDeleteConfirmationOpen && (
        <ConfirmationDialog
          message={`Are you sure you want to delete the stage "${value.name}"? Its data, including its engagement history, will be permanently deleted from the campaign.`}
          onConfirm={deleteStage}
          onClose={closeDeleteConfirmation}
        />
      )}
    </Container>
  );
};

export default forwardRef(Stage);
